// 他参加者のカーソル位置を表示するレイヤ。
// useCanvasViewersStore の cursors を購読し、xRatio/yRatio を stage 座標へ変換して描画する。
// 自分自身のカーソルは OS のマウスカーソルで既に表示されているため、このレイヤでは描画しない。
// （サーバは excludeConnectionId で自分宛て broadcast を抑止しているが、同一ユーザーで
//  複数接続を持つ場合などに備え、クライアント側でも currentUserId を必ず除外する）
//
// カーソル位置の配信は参加人数に応じて 100/200/350ms へ間引かれているため、受信値をそのまま
// 書き出すと受信周期そのままの離散ジャンプに見える。送信周期は変えられない（総配信数が参加
// 人数の 2 乗で増える）ので、受信側の描画だけで見た目の連続性を作る。
// 位置は left/top ではなく transform（translate3d）で与え、遷移は styles.css の
// .canvas-remote-cursor に置いた transition へ委ねる。transform の遷移はブラウザの合成
// スレッドが補間するため、補間のための JavaScript は 1 フレームも走らない。遷移時間だけを
// 要素ごとに CSS カスタムプロパティ --rc-cursor-move-ms で渡す。
// 受信していない先の位置は推測しない（外挿すると急停止・急反転で行き過ぎて引き戻る）。

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { Viewer } from "@refinement-canvas/domain";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../lib/canvas-coordinates";

const CURSOR_STALE_MS = 30_000;
const CURSOR_LAYER_Z_INDEX = Number.MAX_SAFE_INTEGER - 1;

// 受信間隔を実測できない初回の遷移時間（ms）。少人数時の送信間隔（100ms）へ寄せた値。
const CURSOR_MOVE_DEFAULT_MS = 120;
// 遷移時間の下限・上限（ms）。下限は極端に短い遷移で再びカクつくのを防ぎ、
// 上限は遅延が体感されるほど長い遷移を防ぐ。
const CURSOR_MOVE_MIN_MS = 80;
const CURSOR_MOVE_MAX_MS = 400;
// 受信間隔の指数移動平均の係数（新しい観測値の重み）。
const CURSOR_INTERVAL_SMOOTHING = 0.3;
// 画面上でこの距離（px）を超えて飛んだ更新は、遷移させず即時に移す。
const CURSOR_TELEPORT_SCREEN_PX = 400;
// 前回描画からこの時間（ms）を超えて空いた更新は、初回扱いへリセットする。
// 送信間隔（100〜350ms）と静止中の view ハートビート（2 秒）より長い値にする。
const CURSOR_MOTION_RESET_MS = 2_000;

/** ユーザーごとに保持する、遷移時間の算出に必要な直前の反映内容。 */
export interface CursorMotionState {
  /** 直前に反映した updatedAt（受信間隔の実測に使う） */
  readonly lastUpdatedAt: number;
  /** 平滑化済みの受信間隔（ms） */
  readonly intervalMs: number;
  /** 直前に反映したステージ座標 */
  readonly lastStageX: number;
  readonly lastStageY: number;
  /** 直前に返した遷移時間（ms）。同じ入力での再呼び出しへ同じ値を返すために持つ。 */
  readonly lastDurationMs: number;
}

/**
 * 1 回の受信に対する遷移時間（ms）と、次回のための保持状態を求める。
 *
 * 送信側の間引き間隔（100/200/350ms）は受信側から見えないため、受信間隔を実測して
 * 指数移動平均で平滑化した値を遷移時間に使う。平滑値そのものを上下限へ収めて保持するのは、
 * 遷移時間だけをクランプして平滑値を無制限に持つと、長時間の停止をまたいだ 1 回の観測が
 * 平滑値を汚染し、その後の通常間隔の更新でも遷移が遅れ続けるためである。
 *
 * 同じ入力で何度呼ばれても同じ結果を返す（冪等）。React の StrictMode は開発時に effect を
 * 2 回実行するため、冪等でないと初回描画の 2 回目が previous の埋まった状態で評価され、
 * 遷移抑止が失われてカーソルが原点から飛んでくる。
 */
export function resolveCursorMotion(params: {
  readonly previous: CursorMotionState | undefined;
  readonly updatedAt: number;
  readonly stageX: number;
  readonly stageY: number;
  readonly zoom: number;
}): { readonly next: CursorMotionState; readonly durationMs: number } {
  const { previous, updatedAt, stageX, stageY, zoom } = params;
  if (!previous) {
    // 初回描画。遷移させると未設定（原点）から飛んでくるため 0ms にする。
    return {
      next: {
        intervalMs: CURSOR_MOVE_DEFAULT_MS,
        lastDurationMs: 0,
        lastStageX: stageX,
        lastStageY: stageY,
        lastUpdatedAt: updatedAt,
      },
      durationMs: 0,
    };
  }
  // 同じ入力での再呼び出し（StrictMode の二重レンダー・並行レンダーの再実行）では、
  // 状態を進めず直前の結果をそのまま返す。これがないと初回の遷移抑止が 2 回目で失われる。
  if (
    updatedAt === previous.lastUpdatedAt &&
    stageX === previous.lastStageX &&
    stageY === previous.lastStageY
  ) {
    return { next: previous, durationMs: previous.lastDurationMs };
  }
  const observed = updatedAt - previous.lastUpdatedAt;
  // 前回描画から長く空いた更新は「間を置いた再開」であり、同じ動作の続きではない。
  // 初回扱いへ戻して遷移を抑止する。長時間の停止からの再開と、退室 → 再入室
  // （setViewers は cursors を削除しないため store に古い位置が残る）の両方をこの 1 条件で覆う。
  if (observed > CURSOR_MOTION_RESET_MS) {
    return {
      next: {
        intervalMs: CURSOR_MOVE_DEFAULT_MS,
        lastDurationMs: 0,
        lastStageX: stageX,
        lastStageY: stageY,
        lastUpdatedAt: updatedAt,
      },
      durationMs: 0,
    };
  }
  // 逆行・同時刻の updatedAt では観測値を採らず、直前の平滑値を保つ。
  const smoothed =
    observed > 0
      ? previous.intervalMs * (1 - CURSOR_INTERVAL_SMOOTHING) + observed * CURSOR_INTERVAL_SMOOTHING
      : previous.intervalMs;
  const intervalMs = Math.min(CURSOR_MOVE_MAX_MS, Math.max(CURSOR_MOVE_MIN_MS, smoothed));
  // 距離は画面 px で判定する。レイヤはステージ（scale(zoom)）の内側にあるため、
  // ステージ px で判定すると拡大率によって同じ見た目の飛びが抑止されたりされなかったりする。
  const screenDistance =
    Math.hypot(stageX - previous.lastStageX, stageY - previous.lastStageY) * zoom;
  const durationMs = screenDistance > CURSOR_TELEPORT_SCREEN_PX ? 0 : Math.round(intervalMs);
  return {
    next: {
      intervalMs,
      lastDurationMs: durationMs,
      lastStageX: stageX,
      lastStageY: stageY,
      // 逆行した updatedAt をそのまま保持すると、次回の受信間隔がその古い時刻基準になる
      // （逆行幅だけ長い間隔として観測され、CURSOR_MOTION_RESET_MS を超えれば不必要な
      //  初回扱いへ戻る）。単調な時刻を保つことでこれを防ぐ。
      lastUpdatedAt: Math.max(previous.lastUpdatedAt, updatedAt),
    },
    durationMs,
  };
}

/** 1 人分の描画内容。遷移時間まで確定させてからレンダーへ渡す。 */
interface CursorEntry {
  readonly userId: string;
  readonly viewer: Viewer;
  readonly x: number;
  readonly y: number;
  readonly durationMs: number;
  /** 受信時刻。レンダー時にも期限切れを落とすために保持する（下の filter を参照）。 */
  readonly updatedAt: number;
}

interface Props {
  readonly currentUserId: string;
}

export function CanvasCursorsLayer({ currentUserId }: Props): JSX.Element {
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const cursors = useCanvasViewersStore((s) => s.cursors);
  // 保持状態はコミット後にだけ書き換える。useMemo のようにレンダー中へ置くと、React が
  // 破棄したレンダーの座標が残り、次回の遷移時間と飛び判定が「画面に描かれていない位置」を
  // 基準に計算される（並行レンダーでは避けられない）。
  const motionRef = useRef<Map<string, CursorMotionState>>(new Map());
  const [entries, setEntries] = useState<readonly CursorEntry[]>([]);

  // pan / zoom では位置（ステージ座標）は不変のため、view は依存に含めず getState() で都度読む
  // （canvas-remote-carets-layer.tsx と同じ方針）。
  useLayoutEffect(() => {
    const zoom = useCanvasStore.getState().view.zoom;
    const now = Date.now();
    const motions = motionRef.current;
    // 保持状態の回収。cursors から消えたときだけでは足りない。
    // setViewers は cursors を削除しないため、退室後も cursors のエントリは残る。
    for (const userId of [...motions.keys()]) {
      const cursor = cursors[userId];
      if (!cursor || !viewers[userId] || now - cursor.updatedAt > CURSOR_STALE_MS) {
        motions.delete(userId);
      }
    }
    const next: CursorEntry[] = [];
    for (const [userId, cursor] of Object.entries(cursors)) {
      // 自分自身のカーソルは描画しない（他参加者のカーソルと名前ラベルのみ表示する規約）。
      if (userId === currentUserId) continue;
      if (now - cursor.updatedAt > CURSOR_STALE_MS) continue;
      const viewer = viewers[userId];
      if (!viewer) continue;
      const x = cursor.xRatio * CANVAS_WIDTH_PX;
      const y = cursor.yRatio * CANVAS_HEIGHT_PX;
      const motion = resolveCursorMotion({
        previous: motions.get(userId),
        stageX: x,
        stageY: y,
        updatedAt: cursor.updatedAt,
        zoom,
      });
      motions.set(userId, motion.next);
      next.push({
        durationMs: motion.durationMs,
        updatedAt: cursor.updatedAt,
        userId,
        viewer,
        x,
        y,
      });
    }
    // 依存に entries を含めないため、この setState でこの effect は再実行されない（ループしない）。
    setEntries(next);
  }, [cursors, viewers, currentUserId]);

  // 期限切れの判定は、effect（store 更新契機）だけでなくレンダー時にも行う。
  // 親の CanvasStage は view を購読しており、パン / ズームのたびにこのレイヤを再レンダーする。
  // effect の依存は cursors / viewers / currentUserId だけなので、相手の送信が止まったまま
  // 期限へ達しても effect は再実行されない。ここで落とさないと、位置の state 化によって
  // 「再レンダーのたびに期限切れを落とす」という変更前の挙動が失われる。
  const renderNow = Date.now();
  const visibleEntries = entries.filter(
    ({ updatedAt }) => renderNow - updatedAt <= CURSOR_STALE_MS
  );

  return (
    <div
      data-testid="canvas-cursors-layer"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        // カーソルはキャンバス内の図形より上、キャンバス外の操作パネルより下に表示する。
        zIndex: CURSOR_LAYER_Z_INDEX,
      }}
    >
      {visibleEntries.map(({ userId, viewer, x, y, durationMs }) => (
        <div
          key={userId}
          className="canvas-remote-cursor"
          data-testid={`viewer-cursor-${userId}`}
          style={
            {
              position: "absolute",
              // 位置は transform だけで表す。left/top はレイアウトを伴うため、
              // 合成スレッドだけでは遷移させられない。
              left: 0,
              top: 0,
              transform: `translate3d(${x}px, ${y}px, 0)`,
              pointerEvents: "none",
              willChange: "transform",
              "--rc-cursor-move-ms": `${durationMs}ms`,
            } as CSSProperties
          }
        >
          {/* 大きさだけをステージの scale(zoom) から切り離し、ズームに依らず 100% 相当の
              画面サイズで表示する。矢印の指し先（path の (2,2)）を外側のステージ座標へ
              合わせる -2px の補正も、この scale の右側へ置くことで画面 px として効く
              （transform は右の関数が先に点へ適用されるため、scale の右の translate は
               1/zoom されて画面上では常に 2px になる）。 */}
          <div
            data-testid={`viewer-cursor-scale-${userId}`}
            style={{
              transform: "scale(var(--canvas-inv-zoom, 1)) translate(-2px, -2px)",
              transformOrigin: "0 0",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" style={{ display: "block" }}>
              <path
                d="M2,2 L2,16 L6,12 L9,18 L11,17 L8,11 L14,11 Z"
                fill={viewer.color}
                stroke="#ffffff"
                strokeWidth="1"
              />
            </svg>
            <div
              style={{
                marginLeft: "16px",
                marginTop: "-4px",
                background: viewer.color,
                color: "#fff",
                fontSize: "11px",
                padding: "2px 6px",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }}
            >
              {viewer.displayName}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
